import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { ConversionHelper } from '../../helpers/conversion-helper';
import { DialogHelper, DialogWidth } from '@tymes4-shared';
import { SeatPlanHelper } from '../../helpers/seatplan-helper';
import { forkJoin, lastValueFrom } from 'rxjs';
import { ConfirmService } from '@tymes4-shared';
import { HelperService } from '../../components/common/helper-service';
import { HttpLoaderService } from '@tymes4-shared';
import { AutoNumberComponent } from '../auto-number/auto-number.component';
import { SeatPlanRowOffsetComponent } from '../seat-plan-row-offset/seat-plan-row-offset.component';
import { TranslateService } from '@ngx-translate/core';
import { ContingentModel, ContingentService, EventPlacementService, EventPlacementWithStatusView, PlacementType, PlacementTypeService, PriceCategory, PropertyUpdateResult, SeatRestriction, VBBPropertyArgs, VenueBuildingBlockDetail, VenueBuildingBlockService } from '../../api';
import * as jqModule from 'jquery';
import 'jquery.panzoom/dist/jquery.panzoom';
import 'jquery-mousewheel/jquery.mousewheel';

declare var $: any;

// Bridge: copy panzoom plugin from bundled jQuery to global jQuery
const _jq = jqModule as any;
if (typeof $?.fn?.panzoom !== 'function' && typeof _jq.fn?.panzoom === 'function') {
  $.fn.panzoom = _jq.fn.panzoom;
  $.Panzoom = _jq.Panzoom;
}
if (typeof $?.fn?.mousewheel !== 'function' && typeof _jq.fn?.mousewheel === 'function') {
  $.fn.mousewheel = _jq.fn.mousewheel;
}

@Component({
    selector: 'app--seat-plan',
    templateUrl: './edit-seat-plan.component.html',
    styleUrls: ['./edit-seat-plan.component.scss'],
    standalone: false
})
export class EditSeatPlanComponent implements OnInit, AfterViewInit {

  constructor(
    @Inject(MAT_DIALOG_DATA) public passedData: EditSeatPlanDialogData,
    private placementTypeService: PlacementTypeService,
    public dialogRef: MatDialogRef<EditSeatPlanComponent>,
    private venue: VenueBuildingBlockService,
    private loader: HttpLoaderService,
    private eventPlacementService: EventPlacementService,
    private helperService: HelperService,
    private confirmService: ConfirmService,
    private cd: ChangeDetectorRef,
    public readonly convert: ConversionHelper,
    private dialog: MatDialog,
    private contingentService: ContingentService,
    public seatPlanHelper: SeatPlanHelper,
    private translate: TranslateService,
    private sanitizer: DomSanitizer
  ) { }

  @ViewChild('planHolder') planHolder: ElementRef | undefined;
  @ViewChild('seatPlan') seatPlan: ElementRef | undefined;

  private jSeatPlan: JQuerySelectable | undefined;
  private seatsData: EventPlacementWithDirtyFlag[] = this.passedData.payload.endpoints;
  private parentVBB: VenueBuildingBlockDetail = this.passedData.payload.ParentVBB;
  private newSelectedEp: EventPlacementWithDirtyFlag[] | null = null;
  private seatPlanIsInitialized: boolean = false;
  private priceCategoryColorMap: Map<number, string> = new Map();

  public selectedEndpoints: EventPlacementWithDirtyFlag[] = [];
  public isDragging: boolean = false;
  public currentHoveredSeat: EventPlacementWithDirtyFlag | null = null;
  public tooltipPosition = { x: 0, y: 0 };
  public eventPlacementEventId: number | null = this.passedData.payload.eventPlacementEventId;
  public rows: SeatPlanRow[] | null = null;
  public saving: boolean = false;
  public isDirty: boolean = false;
  public placementTypes: PlacementType[] = [];
  public seatRestrictionTypes: SeatRestriction[] = [];
  public contingents: ContingentModel[] = [];
  public priceCategories: PriceCategory[] = [];
  public selectedContingentId: number = -1;
  public selectedSeatRestrictionTypeId: number = -1;
  public selectedPriceCategoryId: number = -1;
  public selectedPlacementTypeId: number = -1;
  public qualityInput: number = 1;
  public isQualityMode: boolean = false;
  public isLegendVisible: boolean = false;
  public horizontalStep: number = 25;
  public verticalStep: number = 40;
  public hoveredPriceCategoryId: number | null = null;
  public hoveredRestrictionId: number | null = null;
  public hoveredContingentId: number | null = null;
  public activeSeatRestrictions: SeatRestriction[] = [];
  public activeContingents: ContingentModel[] = [];
  public activePriceCategories: PriceCategory[] = [];
  
  public readonly bestSeatQuality: number = 1000;


  ngOnInit() {
    this.jSeatPlan = $("#seatPlan") as unknown as JQuerySelectable;

    this.loadData();

    this.convertDataToRows();
  }

  ngAfterViewInit() {
    this.addZoomAndPan();
  }

  clearSelection() {
    this.selectedEndpoints = [];
    this.setSeatPlanProperties(true);
  }

  determineRowName(row: SeatPlanRow): string | null {
    if (!row?.columns?.length || row.idx === 0) {
      return '';
    }

    let rowNumber: string | null = null;

    for (const col of row.columns) {
      if (col.data?.PlacementTypeId === 1 && col.data.RowNumber != null) {
        if (rowNumber === null) {
          rowNumber = col.data.RowNumber;
        } else if (rowNumber !== col.data.RowNumber) {
          return rowNumber + "*";
        }
      }
    }

    return rowNumber;
  }

  getSeatTooltipContent(): string {
    const seat = this.currentHoveredSeat;
    if (!seat) return '';

    const lines: string[] = [];

    if (seat.RowNumber || seat.SeatNumber) {
      lines.push(`${this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.ROW')}: ${seat.RowNumber || '-'}`);
      lines.push(`${this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.SEAT')}: ${seat.SeatNumber || '-'}`);
    }

    if (seat.PriceCategoryId !== null && seat.PriceCategoryId !== undefined) {
      const priceCategory = this.priceCategories.find(pc => pc.Id === seat.PriceCategoryId);
      if (priceCategory) {
        lines.push(`${this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.PRICE-CATEGORY')}: ${priceCategory.Name}`);
      }
    }

    if (seat.PlacementTypeId !== null && seat.PlacementTypeId !== undefined) {
      const placementType = this.placementTypes.find(pt => pt.Id === seat.PlacementTypeId);
      if (placementType) {
        lines.push(`${this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.PLACEMENT-TYPE')}: ${placementType.Name}`);
      }
    }

    if (seat.SeatRestrictionId !== null && seat.SeatRestrictionId !== undefined && seat.SeatRestrictionId !== 0) {
      const seatRestriction = this.seatRestrictionTypes.find(sr => sr.Id === seat.SeatRestrictionId);
      if (seatRestriction) {
        lines.push(`${this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.SEAT-RESTRICTION')}: ${seatRestriction.Name}`);
      }
    }

    if (seat.ContingentId !== null && seat.ContingentId !== undefined && seat.ContingentId !== 0) {
      const contingent = this.contingents.find(c => c.Id === seat.ContingentId);
      if (contingent) {
        lines.push(`${this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.CONTINGENT')}: ${contingent.Name}`);
      }
    }

    if (seat.Quality) {
      lines.push(`${this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.QUALITY')}: ${seat.Quality}`);
    }

    const statusFlags: string[] = [];
    if (seat.Sold) statusFlags.push(this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.SOLD'));
    if (seat.Reserved) statusFlags.push(this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.RESERVED'));
    if (seat.Unavailable) statusFlags.push(this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.UNAVAILABLE'));
    if (seat.InShoppingCart) statusFlags.push(this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.IN-CART'));

    if (statusFlags.length > 0) {
      lines.push(`${this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.TOOLTIP.STATUS')}: ${statusFlags.join(', ')}`);
    }

    return lines.join('\n');
  }

  onSeatHover(seat: EventPlacementWithDirtyFlag, event: MouseEvent): void {
    if (seat.PlacementTypeId !== PlacementTypeEnum.Seat) {
      return;
    }

    this.currentHoveredSeat = seat;
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    this.tooltipPosition = {
      x: rect.left + (rect.width / 2),
      y: rect.top - 8
    };
  }

  selectByProperty(prop: string, value: number | string | null): void {
    this.selectedEndpoints = [];

    if (!this.rows?.length) return;

    let normalizedValue = value === -1 ? null : value;
    
    if (normalizedValue === 0 && (prop === 'ContingentId' || prop === 'SeatRestrictionId' || prop === 'PriceCategoryId')) {
      normalizedValue = null;
    }

    for (const row of this.rows) {
      for (const column of row.columns) {
        if (column.data && column.data[prop as keyof EventPlacementWithDirtyFlag] === normalizedValue) {
          this.selectedEndpoints.push(column.data);
        }
      }
    }

    this.setSeatPlanProperties(true);
  }

  setProperty(prop: keyof EventPlacementWithDirtyFlag, value: number | string | null): void {
    if (this.rows == null) return;

    let normalizedValue = value === -1 ? null : value;
    
    if (normalizedValue === 0 && (prop === 'ContingentId' || prop === 'SeatRestrictionId' || prop === 'PriceCategoryId')) {
      normalizedValue = null;
    }
    
    this.isDirty = true;

    this.selectedEndpoints.forEach(ep => {
      (ep as Record<string, unknown>)[prop] = normalizedValue;
      ep.dirty = true;
    });

    this.selectedEndpoints = [];
    this.initializeLegendColorMaps();
    this.setSeatPlanProperties(false);
  }

  storeChanges() {
    if (this.saving) return;

    this.saving = true;
    this.loader.open();

    const data: VBBPropertyArgs = {
      OrientationDegrees: 0,
      Scale: this.parentVBB.Scale != null ? this.parentVBB.Scale : 100,
      UpdatableChildren: this.seatsData.filter(function (ep) {
        if (ep.dirty) {
          return ep;
        }
      })
    };

    const id = this.parentVBB.Id!;

    const observable = this.eventPlacementEventId == null ? this.venue.updateProperties(id, data) : this.eventPlacementService.putEventPlacementProperties(id, data);

    observable.subscribe({
      next: (data: PropertyUpdateResult) => {
        if (data && data.Warnings && data.Warnings !== null && data.Warnings.length > 0) {

          let curWarning = 0;
          let seatWarnings = '';
          const nrOfWarnings = data.Warnings.length;


          for (const w of data.Warnings) {

            if (curWarning > 3) {

              const remaining = (nrOfWarnings - curWarning);

              if (remaining == 1) {
                seatWarnings += this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.SEAT-WARNINGS.ROW') + w.RowNumber + this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.SEAT-WARNINGS.SEAT') + w.SeatNumber;
              } else if (remaining > 1) {
                seatWarnings += '<br />- ' + remaining + this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.SEAT-WARNINGS.OTHER');
              }

              break;
            }

            seatWarnings += this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.SEAT-WARNINGS.ROW') + w.RowNumber + this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.SEAT-WARNINGS.SEAT') + w.SeatNumber;
            curWarning++;
          }

          this.confirmService.confirm({
            title: this.translate.instant('GENERIC.CONFIRM.TITLE.WARNING'),
            message: this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.CONFIRM.WARNING.MESSAGE.CHANGES-SAVED') + seatWarnings,
            okonly: true
          }, 'auto').subscribe();

        }
        this.resetChangeState();
      },
      error: (error) => {
        this.loader.close();
        this.saving = false;
        this.confirmService.confirm({
          title: this.translate.instant('GENERIC.CONFIRM.TITLE.ERROR'),
          message: this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.ERROR.SAVE-FAILED'),
          okonly: true
        }, 'auto').subscribe();
      }
    });
  }

  close() {
    this.dialogRef.close(null);
  }

  confirmAutoNumber() {

    const options = DialogHelper.GetDialogOptions(DialogWidth.md, {
      title: this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.CONFIRM.TITLE.ENABLE-AUTOMATIC-SEAT-NUMBERING'),
      rows: this.rows
    });
    const dialogRef: MatDialogRef<AutoNumberComponent> = this.dialog.open(AutoNumberComponent, options);

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.isDirty = true;
        this.setSeatPlanProperties(false);
      }
    });
  }

  confirmRowOffset() {
    if (this.selectedEndpoints.length != 1) return;

    const title = this.translate.instant('DIALOGS.EDIT-SEAT-PLAN.CONFIRM.TITLE.DIFFERENT-SET-OF-SEATNUMBERS');
    const dialogRef: MatDialogRef<SeatPlanRowOffsetComponent> = this.dialog.open(SeatPlanRowOffsetComponent,
      {
        panelClass: 'full-width-dialog',
        disableClose: false,
        width: '650px',
        maxWidth: '98vw',
        data: {
          title: title
        }
      });


    dialogRef.afterClosed().subscribe(data => {
      if (data !== null) {
        this.offsetNumbering(this.selectedEndpoints[0], data.StartNumber, data.LTR)
      }
    });
  }

  getPriceCategoryBackgroundColor(priceCategoryId: number | null | undefined): string | null {
    if (priceCategoryId === null || priceCategoryId === undefined || priceCategoryId === 0) {
      return null;
    }
    return this.priceCategoryColorMap.get(priceCategoryId) || null;
  }

  getContingentColor(contingent: ContingentModel | null | undefined): string | null {
    if (!contingent || !contingent.HexColor) {
      return null;
    }
    return contingent.HexColor;
  }

  isSeatHighlighted(seat: EventPlacementWithDirtyFlag | null): boolean {
    if (!seat) return false;

    if (this.hoveredPriceCategoryId !== null && seat.PriceCategoryId === this.hoveredPriceCategoryId) {
      return true;
    }

    if (this.hoveredRestrictionId !== null && seat.SeatRestrictionId === this.hoveredRestrictionId) {
      return true;
    }

    if (this.hoveredContingentId !== null && seat.ContingentId === this.hoveredContingentId) {
      return true;
    }

    return false;
  }

  toggleQualityMode(): void {
    this.isQualityMode = !this.isQualityMode;
  }

  toggleLegend(): void {
    this.isLegendVisible = !this.isLegendVisible;
  }

  bulkUpdateQuality() {

    this.selectedEndpoints.forEach((seat) => {
      const seatData = this.seatsData.find(s => s.Id === seat.Id);
      if (seatData) {
        seatData.Quality = this.qualityInput || 0;
        seatData.dirty = true;
      }
    });

    this.isDirty = true;
    this.clearSelection();
  }

  resetQuality() {
    this.confirmService.confirm({
      title: this.translate.instant('GENERIC.CONFIRM.TITLE.WARNING'),
      message: this.translate.instant('SEATPLAN.RESET_QUALITY_CONFIRM')
    }, 'auto').subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.seatsData.forEach(seat => {
          seat.Quality = null;
          seat.dirty = true;
        });

        this.isDirty = true;
        this.clearSelection();
      }
    });
  }

  calculateBestSeats() {
    if (this.selectedEndpoints.length === 0) {
      return;
    }

    this.selectedEndpoints.forEach((seat) => {
      const seatData = this.seatsData.find(s => s.Id === seat.Id);
      if (seatData) {
        seatData.Quality = this.bestSeatQuality;
        seatData.dirty = true;
      }
    });

    const bestSeatIds = new Set(this.selectedEndpoints.map(s => s.Id));

    this.seatsData.forEach((seat) => {
      if (bestSeatIds.has(seat.Id) || seat.PlacementTypeId !== PlacementTypeEnum.Seat) {
        return;
      }

      let minDistance = Infinity;

      this.selectedEndpoints.forEach((bestSeat) => {
        const horizontalDistance = Math.abs((seat.Column ?? 0) - (bestSeat.Column ?? 0));
        const verticalDistance = Math.abs((seat.Row ?? 0) - (bestSeat.Row ?? 0));

        const weightedDistance = (horizontalDistance * this.horizontalStep) + (verticalDistance * this.verticalStep);

        if (weightedDistance < minDistance) {
          minDistance = weightedDistance;
        }
      });

      const calculatedQuality = Math.max(1, this.bestSeatQuality - minDistance);
      seat.Quality = calculatedQuality;
      seat.dirty = true;
    });

    this.isDirty = true;
    this.clearSelection();
  }

  getQualityStyleSafe(seat: EventPlacementWithDirtyFlag): SafeStyle {
    const quality = seat.Quality ?? null
    const backgroundColor = this.getQualityColor(quality, seat);
    return this.sanitizer.bypassSecurityTrustStyle(`background: ${backgroundColor}; color: white;`);
  }

  private addZoomAndPan() {
    const $section = $('#focal');
    const $panzoomEl = $section.find('.panzoom-parent');
    const $panzoom = ($panzoomEl as JQueryPanzoom).panzoom({
      which: 3,
      cursor: 'pointer',
    });

    $panzoom.on('panzoomstart', () => {
      $panzoom.css('cursor', 'grabbing');
      this.isDragging = true;
      this.cd.detectChanges();
    });

    $panzoom.on('panzoomend', () => {
      $panzoom.css('cursor', 'pointer');
      this.isDragging = false;
      this.cd.detectChanges();
    });

    // Manual right-click pan handler — panzoom's internal event binding
    // is unreliable with jQuery 3.7+ and modern browsers
    $panzoomEl.on('contextmenu.custompan', (e) => {
      e.preventDefault();
    });
    $panzoomEl.on('mousedown.custompan', (e) => {
      if ((e as any).which !== 3) return;
      e.preventDefault();
      (e as any).stopPropagation();

      $panzoom.css('cursor', 'grabbing');
      this.isDragging = true;
      this.cd.detectChanges();

      const startX = (e as any).pageX;
      const startY = (e as any).pageY;
      const matrix = $panzoom.panzoom('getMatrix');
      const origX = +matrix[4];
      const origY = +matrix[5];

      $(document)
        .on('mousemove.custompan', (moveEvt) => {
          moveEvt.preventDefault();
          $panzoom.panzoom('pan',
            origX + (moveEvt as any).pageX - startX,
            origY + (moveEvt as any).pageY - startY,
            { animate: 'skip' }
          );
        })
        .on('mouseup.custompan', () => {
          $(document).off('.custompan');
          $panzoom.css('cursor', 'pointer');
          this.isDragging = false;
          this.cd.detectChanges();
        });
    });

    $panzoom.parent().on('mousewheel.focal', (e: JQuery.Event) => {
      e.preventDefault();
      const jqEvent = e as JQuery.TriggeredEvent & { delta?: number; originalEvent?: WheelEvent & { wheelDelta?: number } };
      const delta = jqEvent.delta ?? jqEvent.originalEvent?.wheelDelta ?? -(jqEvent.originalEvent?.deltaY ?? 0);
      const zoomOut = delta < 0;

      $panzoom.panzoom('zoom', zoomOut, {
        increment: 0.1,
        animate: false,
        focal: e
      });
    });
  }

  private async loadData() {
    this.loader.open();

    try {
      const noneText = this.translate.instant('GENERIC.DROPDOWN.NONE');

      const [priceCategories, placementTypes, seatRestrictionTypes, contingents] =
        await lastValueFrom(forkJoin([
          this.venue.listSelectablePriceCategories(this.parentVBB.Id!),
          this.placementTypeService.getAllPlacementTypes(),
          this.venue.listSeatRestrictions(),
          this.contingentService.listSelectableContingents()
        ]));

      this.priceCategories = priceCategories;
      this.helperService.setInheritedPriceCategoryNames(this.priceCategories);

      this.placementTypes = placementTypes;

      this.seatRestrictionTypes = [{ Id: 0, Name: noneText }, ...seatRestrictionTypes];

      this.contingents = [{ Id: 0, Name: noneText }, ...contingents];

      this.initializeLegendColorMaps();
      this.initializeSelectable();

    } finally {
      this.loader.close();
    }
  }

  private convertDataToRows() {
    const maxRow = Math.max(...this.seatsData.map(r => r.Row ?? 0));
    const maxCol = Math.max(...this.seatsData.map(r => r.Column ?? 0));

    this.rows = [];
    this.clearSelection();

    const seatLookup = new Map<string, EventPlacementWithDirtyFlag>();
    for (const seat of this.seatsData) {
      if (seat.Row != null && seat.Column != null) {
        const key = `${seat.Row}-${seat.Column}`;
        seatLookup.set(key, seat);
      }
    }

    for (let r = 1; r <= maxRow; r++) {
      const columns: SeatPlanColumn[] = [];

      for (let c = 0; c <= maxCol; c++) {
        const key = `${r}-${c}`;
        const seatData = seatLookup.get(key) ?? null;

        columns.push({
          data: seatData,
          idx: c
        });
      }

      this.rows.push({
        columns: columns,
        idx: r
      });
    }
  }

  private initializeSelectable(): void {
    this.setSeatPlanProperties(this.seatPlanIsInitialized);
    if (!this.seatPlanIsInitialized) this.seatPlanIsInitialized = true;

    (this.jSeatPlan as JQuerySelectable).selectable({
      filter: '.sp-seat-selectable',
      start: () => this.onSelectionStart(),
      selected: (event: SelectableEvent, ui: SelectableUI) => this.onItemSelect(event, ui),
      stop: (event: SelectableEvent, ui: SelectableUI) => this.onSelectionEnd(event, ui)
    });

    this.centerSeatPlanView();
  }

  private centerSeatPlanView() {
    if (!this.planHolder || !this.seatPlan) {
      return;
    }

    this.cd.detectChanges();  // Force change detection to ensure DOM is updated

    const planHolderEl = this.planHolder.nativeElement;
    const seatPlanEl = this.seatPlan.nativeElement;

    seatPlanEl.style.position = 'absolute';
    seatPlanEl.style.left = `${(planHolderEl.offsetWidth - seatPlanEl.offsetWidth) / 2}px`;
    seatPlanEl.style.top = `${(planHolderEl.offsetHeight - seatPlanEl.offsetHeight) / 2}px`;
  }

  private clear() {
    (this.jSeatPlan as JQuerySelectable).selectable("destroy");
    this.initializeSelectable();
  }

  private setSeatPlanProperties(onlyUpdateSelectable: boolean) {
    this.rows!.forEach((row: SeatPlanRow, rowIdx: number) => {
      row.rowName = this.determineRowName(row);

      row.columns.forEach((column: SeatPlanColumn, colIdx: number) => {
        if (column.data !== null) {
          column.selected = this.isItemSelected(column.data);

          if (!onlyUpdateSelectable) {
            column.Classes = this.determineEntpointCss(column);
            column.SeatName = this.convert.namingForSeat(column.data, colIdx, rowIdx);
            column.artifectStyle = this.seatPlanHelper.getArtifactStyle(
              this.contingents,
              column.data.PlacementTypeId,
              column.data.Sold,
              column.data.ContingentId,
              column.data.ReservationHexColor,
              true,
              this.getPriceCategoryBackgroundColor(column.data.PriceCategoryId)
            );
          }
        }
      });
    });
  }

  private isItemSelected(column: EventPlacementWithDirtyFlag | null): boolean {
    if (!column || this.selectedEndpoints == null || this.selectedEndpoints.length === 0) return false;

    return this.selectedEndpoints.some(ep => ep.Id === column.Id);
  }

  private determineEntpointCss(col: SeatPlanColumn): string {
    const endpoint = col.data;
    if (!endpoint) return '';

    const classes: string[] = ['sp-h', 'sp-seat-selectable'];

    const placementTypeClasses: Record<number, string> = {
      2: 's-stair',
      3: 'endpoint-noseat',
      4: 'endpoint-entrance',
      5: 's-fence'
    };

    const placementClass = placementTypeClasses[endpoint.PlacementTypeId!];
    if (placementClass) {
      classes.push(placementClass);
      classes.push('no-hover-effect');
    } else if (endpoint.PlacementTypeId === 1) {
      if (endpoint.SeatRestrictionId !== null) {
        classes.push('s-rv');
      }
    } else if (endpoint.PlacementTypeId && this.eventPlacementEventId !== null) {
      if (endpoint.Unavailable) {
        classes.push('s-not-available-in-edit');
      } else if (endpoint.SeatRestrictionId !== null) {
        classes.push('s-rv');
      }
    }

    if (this.isItemSelected(endpoint)) {
      classes.push('sp-sel');
    }

    return classes.join(' ');
  }

  private onSelectionStart() {
    this.cd.detach();
    this.newSelectedEp = [];
  }

  private onItemSelect(e: SelectableEvent, element: SelectableUI) {
    const id: number = +element.selected.id;
    const found = this.seatsData.find((ep) => ep.Id === id);

    if (this.newSelectedEp && found) {
      this.newSelectedEp.push(found);
    }
  }

  private onSelectionEnd(e: SelectableEvent, element: SelectableUI): void {
    try {
      if (this.newSelectedEp === null) {
        return;
      }

      const isAlternateMode = e.ctrlKey || this.newSelectedEp.length === 1;

      if (!isAlternateMode) {
        this.clearSelection();
      }

      for (const newEp of this.newSelectedEp) {
        const isSelected = this.isItemSelected(newEp);

        if (isSelected && isAlternateMode) {
          this.selectedEndpoints = this.selectedEndpoints.filter(ep => ep.Id !== newEp.Id);
        } else if (!isSelected) {
          this.selectedEndpoints.push(newEp);
        }
      }

      this.newSelectedEp = [];
      this.clear();
      this.cd.detectChanges();
    } finally {
      this.cd.reattach();
    }
  }

  private resetChangeState(): void {
    this.isDirty = false;

    for (const ep of this.seatsData) {
      ep.dirty = false;
    }

    this.loader.close();
    this.saving = false;
    this.close();
  }

  private offsetNumbering(offSetEndpoint: EventPlacementWithDirtyFlag, startNr: number, ltr: boolean): void {
    const row = this.rows![offSetEndpoint.Row! - 1];
    let seatNumber = startNr;
    const increment = ltr ? 1 : -1;
    let hasChanges = false;

    for (const col of row.columns) {
      if (col.data && col.data.Column! >= offSetEndpoint.Column! && col.data.PlacementTypeId === 1) {
        col.data.SeatNumber = seatNumber.toString();
        col.data.dirty = true;
        hasChanges = true;
        seatNumber += increment;
      }
    }

    if (hasChanges) {
      this.isDirty = true;
      this.setSeatPlanProperties(false);
    }
  }

  private initializeLegendColorMaps(): void {
    const priceCategoryPalette = [
      '#65913E',
      '#98C471',
      '#B2D395',
      '#CBE1B8'
    ];

    this.getActivePriceCategories();

    this.activePriceCategories.forEach((category, index) => {
      const colorIndex = index % priceCategoryPalette.length;
      this.priceCategoryColorMap.set(category.Id!, priceCategoryPalette[colorIndex]);
    });

    this.getActiveRestrictions();
    this.getActiveContingents();
  }

  private getQualityColor(quality: number | undefined | null, seat: EventPlacementWithDirtyFlag): string {
    if (seat.PlacementTypeId !== PlacementTypeEnum.Seat) {
      return '';
    }

    if (quality === null || quality === undefined) return '#cccccc';
    else if (quality === this.bestSeatQuality) return '#007bff';

    const normalized = Math.min(Math.max(quality / this.bestSeatQuality, 0), 1);

    if (normalized < 0.5) {
      const r = 255;
      const g = Math.floor(255 * (normalized * 2));
      return `rgb(${r}, ${g}, 0)`;
    } else {
      const r = Math.floor(255 * (1 - (normalized - 0.5) * 2));
      const g = 255;
      return `rgb(${r}, ${g}, 0)`;
    }
  }

  private getActivePriceCategories() {
    const activePriceCategoryIds = new Set<number>();

    for (const seat of this.seatsData) {
      if (seat.PriceCategoryId !== null && seat.PriceCategoryId !== undefined && seat.PriceCategoryId !== 0) {
        activePriceCategoryIds.add(seat.PriceCategoryId);
      }
    }

    this.activePriceCategories = this.priceCategories.filter(pc => activePriceCategoryIds.has(pc.Id!));
  }

  private getActiveRestrictions() {
    const activeRestrictionIds = new Set<number>();

    for (const seat of this.seatsData) {
      if (seat.SeatRestrictionId !== null && seat.SeatRestrictionId !== undefined && seat.SeatRestrictionId !== 0) {
        activeRestrictionIds.add(seat.SeatRestrictionId);
      }
    }

    this.activeSeatRestrictions = this.seatRestrictionTypes.filter(sr => activeRestrictionIds.has(sr.Id!));
  }

  private getActiveContingents() {
    const activeContingentIds = new Set<number>();

    for (const seat of this.seatsData) {
      if (seat.ContingentId !== null && seat.ContingentId !== undefined && seat.ContingentId !== 0) {
        activeContingentIds.add(seat.ContingentId);
      }
    }

    this.activeContingents = this.contingents.filter(c => activeContingentIds.has(c.Id!));
  }

}


interface EventPlacementWithDirtyFlag extends EventPlacementWithStatusView {
  dirty?: boolean;
}

interface SeatPlanColumn {
  data: EventPlacementWithDirtyFlag | null;
  idx: number;
  selected?: boolean;
  Classes?: string;
  SeatName?: string;
  artifectStyle?: SafeStyle;
}

interface SeatPlanRow {
  columns: SeatPlanColumn[];
  idx: number;
  rowName?: string | null;
}

interface EditSeatPlanDialogData {
  title: string,
  payload: {
    endpoints: EventPlacementWithStatusView[];
    ParentVBB: VenueBuildingBlockDetail;
    eventPlacementEventId: number | null;
  };
}

interface SelectableEvent extends Event {
  ctrlKey: boolean;
}

interface SelectableUI {
  selected: HTMLElement & { id: string };
}

interface JQueryPanzoom extends JQuery {
  panzoom(options: Record<string, unknown>): JQueryPanzoom;
  panzoom(method: string, ...args: unknown[]): JQueryPanzoom;
}

interface JQuerySelectable extends JQuery {
  selectable(options: Record<string, unknown>): JQuerySelectable;
  selectable(method: string): JQuerySelectable;
}

enum PlacementTypeEnum {
  Seat = 1,
  Stairs = 2,
  NoSeat = 3
}